import { Dialog } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  TokenConsolidationDialogComponent,
  TokenConsolidationDialogData,
} from '../../components/token-consolidation-dialog/token-consolidation-dialog.component';
import {
  Kcc20ConsolidationAsset,
  Kcc20ConsolidationStatus,
  Kcc20TokenActionApiService,
} from './kcc20-token-action-api.service';
import {
  Kcc20TokenActionRequest,
  Kcc20WalletOperationQueueService,
} from '../wallet-operations/kcc20-wallet-operation-queue.service';
import { formatKcc20BaseUnitsForDisplay } from '../../shared/kcc20-token-amount';
import { WalletManager } from '../../managers/wallet.manager';
import { WALLET_OPERATION_LABELS } from '../wallet-operations/wallet-operation-title.util';

interface ConsolidationWalletContext {
  walletAddress: string;
  owner: string;
}

export interface Kcc20ConsolidationFlowRequest extends Omit<
  Kcc20TokenActionRequest,
  'build'
> {
  asset: Kcc20ConsolidationAsset;
  tokenAmount: string;
  tokenLabel: string;
  wrappedMarketId?: string;
  operation?: 'spend' | 'unwrap';
  consolidateOnly?: boolean;
  consolidateAll?: boolean;
  build?: Kcc20TokenActionRequest['build'];
  buildForTokenAmount?: (
    tokenAmount: string,
  ) => ReturnType<Kcc20TokenActionRequest['build']>;
  onQueued?: (actionId: string) => void;
  onCancelled?: () => void;
  /**
   * Marks every action/pending-flow card this flow creates as internal (see
   * {@link Kcc20TokenActionRequest.internal}), so it never appears in the
   * global wallet-operations tray — the caller owns its own progress UI and
   * uses {@link onProgress} to drive it instead.
   */
  internal?: boolean;
  /**
   * Fired with a human-readable status string at each stage of an internal
   * flow (checking, queued, preparing, "Opening wallet…", "Approve in your
   * wallet…", etc.) — the same narrative the pending-flow/queue cards would
   * otherwise show, redirected here since {@link internal} suppresses them.
   * Ignored unless {@link internal} is set.
   */
  onProgress?: (message: string) => void;
}

/**
 * One accepted click, from acknowledgement to execution: it owns the overlay
 * placeholder card and, once confirmed, a slot in its token's flow lane.
 */
interface PendingFlowTicket {
  tokenKey: string;
  asset: Kcc20ConsolidationAsset;
  cardId: string;
  /** Set when the user dismisses the card; every await checks it after. */
  cancelled: boolean;
  resolveTurn?: (outcome: 'run' | 'cancelled') => void;
  /** See {@link Kcc20ConsolidationFlowRequest.onProgress}. */
  onProgress?: (message: string) => void;
}

@Injectable({ providedIn: 'root' })
export class Kcc20TokenConsolidationFlowService {
  private static readonly MAX_MERGES_PER_FLOW = 64;
  private static readonly PROJECTION_POLL_ATTEMPTS = 30;
  private static readonly PROJECTION_POLL_DELAY_MS = 1_000;
  /**
   * Clicks on the same token+asset+title within this window are treated as one:
   * every Bucket B click is a real spend, so a fumbled double-click must not
   * queue two operations (#237). Deliberate repeat purchases click slower than
   * this; each of those queues its own flow.
   */
  private static readonly DOUBLE_CLICK_IGNORE_MS = 300;

  private readonly dialog = inject(Dialog);
  private readonly tokenActionApi = inject(Kcc20TokenActionApiService);
  private readonly queue = inject(Kcc20WalletOperationQueueService);
  private readonly walletManager = inject(WalletManager);

  /**
   * Flow lanes, keyed per TOKEN (not token+asset): one flow replays at a time
   * per token, in click order, while different tokens proceed in parallel —
   * the same serialization rule the action queue applies to its builds.
   */
  private readonly activeFlows = new Map<string, Kcc20ConsolidationAsset>();
  private readonly queuedFlows = new Map<string, PendingFlowTicket[]>();
  /**
   * Tickets still in their click-time preflight (status check / dialog),
   * before they join a lane. Counted by {@link isRunning} so the Consolidate
   * buttons gate from the moment of the click, exactly as the old
   * synchronous `runningKeys` guard did.
   */
  private readonly preflightFlows = new Set<PendingFlowTicket>();
  /** Snapshot of preflighting+active+queued flows for reactive consumers (isRunning). */
  private readonly flowKeys = signal<
    ReadonlyMap<string, ReadonlySet<Kcc20ConsolidationAsset>>
  >(new Map());
  private readonly lastAcceptedClickAt = new Map<string, number>();
  /**
   * Live progress callbacks for internal actions currently in the queue
   * (build→sign→submit), keyed by the queue action id. Populated by
   * {@link awaitIndexed}, drained by the effect below as `queue.operations`
   * changes — this is how an internal flow's caller sees "Opening wallet…" /
   * "Approve in your wallet…" without a visible tray card.
   */
  private readonly progressWatchers = new Map<
    string,
    (message: string) => void
  >();

  constructor() {
    // Replay the next queued flow whenever the wallet-operation world moves —
    // a token's lane frees when its last action leaves the queue/tracker.
    // Optional-chained because unit tests stub the queue with plain objects.
    effect(() => {
      const ops = this.queue.operations?.();
      untracked(() => {
        this.maybeDrainFlows();
        if (ops) {
          this.forwardProgress(ops);
        }
      });
    });
  }

  private forwardProgress(
    ops: readonly { actionId?: string; id: string; message: string }[],
  ): void {
    for (const [actionId, onProgress] of this.progressWatchers) {
      const op = ops.find(
        (candidate) =>
          candidate.actionId === actionId || candidate.id === actionId,
      );
      if (op) {
        onProgress(op.message);
      }
    }
  }

  /**
   * True from the moment a click for this token+asset is accepted until its
   * flow finishes — spanning preflight (status check / dialog), queued wait,
   * and replay. No longer a gate for the Bucket B action buttons (those stay
   * live and queue flows); still drives the explicit Consolidate buttons,
   * where queueing a second pass behind a running one has nothing left to do.
   */
  isRunning(tokenId: string, asset: Kcc20ConsolidationAsset): boolean {
    return this.flowKeys().get(this.tokenKey(tokenId))?.has(asset) ?? false;
  }

  /**
   * Run a consolidation-backed token action as a queued flow (#237):
   *
   * 1. The click is acknowledged immediately with a card in the
   *    wallet-operations overlay.
   * 2. The status check and (when preparation is needed) the confirmation
   *    dialog run at CLICK time, so the user approves up front and the rest
   *    runs unattended.
   * 3. The confirmed flow waits for its token's lane — one flow per token at
   *    a time, in click order; other tokens are unaffected — and then replays
   *    against a fresh status snapshot, without re-showing the dialog.
   *
   * The returned promise settles when the flow finishes (its final action is
   * enqueued/indexed), rejects on flow-level failure, and resolves undefined
   * when the click was debounced, cancelled, or there was nothing to do.
   */
  async execute(
    request: Kcc20ConsolidationFlowRequest,
  ): Promise<string | undefined> {
    const tokenKey = this.tokenKey(request.tokenId);
    if (this.isDoubleClick(tokenKey, request)) {
      return undefined;
    }

    const ticket: PendingFlowTicket = {
      tokenKey,
      asset: request.asset,
      cardId: '',
      cancelled: false,
      onProgress: request.onProgress,
    };
    const initialMessage = 'Checking whether balance preparation is needed…';
    ticket.cardId = this.queue.registerPendingFlow({
      tokenId: request.tokenId,
      title: request.title,
      message: initialMessage,
      onCancel: () => this.cancelTicket(ticket),
      internal: request.internal,
    });
    request.onProgress?.(initialMessage);
    this.preflightFlows.add(ticket);
    this.syncFlowKeys();
    // The originating action's placeholder (wrap, unwrap, sell, …) stays in
    // the overlay while prepare-balance merges run, so consolidation appears
    // as a new card on top instead of renaming this one. It is released when
    // the originating action is enqueued, when the flow ends, or on cancel.
    let cardHeld = true;
    const handOffCard = () => {
      if (cardHeld) {
        cardHeld = false;
        this.queue.releasePendingFlow(ticket.cardId);
      }
    };
    let laneHeld = false;
    // True when a real queued action (merge or originating) already failed in
    // the overlay — do not also record a wrap/sell preflight card for it.
    let queuedActionFailed = false;

    try {
      const walletContext = this.captureWalletContext();

      // ---- Click-time preflight: status check + user confirmation. ----
      let remainingTokenAmount = request.tokenAmount;
      let status = await this.loadStatus(request, remainingTokenAmount);
      if (request.consolidateOnly && status.reason === 'incompatible_sources') {
        ({ status, tokenAmount: remainingTokenAmount } =
          await this.retargetCompatibleBalance(
            request,
            status,
            remainingTokenAmount,
          ));
      }
      if (ticket.cancelled) {
        request.onCancelled?.();
        return undefined;
      }
      this.assertWalletContext(walletContext);
      if (request.consolidateOnly && status.reason === 'not_required') {
        handOffCard();
        return undefined;
      }
      if (status.reason !== 'not_required') {
        this.assertActionable(request, status);
        this.assertTransactionLimit(status, 0, request.consolidateOnly);

        const confirmed = await firstValueFrom(
          this.dialog.open<
            boolean,
            TokenConsolidationDialogData,
            TokenConsolidationDialogComponent
          >(TokenConsolidationDialogComponent, {
            width: 'min(440px, 94vw)',
            data: {
              tokenLabel: request.tokenLabel,
              requestedTokenAmount: remainingTokenAmount,
              status,
              consolidateOnly: request.consolidateOnly ?? false,
              consolidateAll: request.consolidateAll ?? false,
            },
          }).closed,
        );
        if (ticket.cancelled || !confirmed) {
          handOffCard();
          request.onCancelled?.();
          return undefined;
        }
        this.assertWalletContext(walletContext);
      }

      // ---- Wait for the token's flow lane (click order, one at a time). ----
      if ((await this.waitForFlowTurn(ticket)) === 'cancelled') {
        request.onCancelled?.();
        return undefined;
      }
      laneHeld = true;

      // ---- Replay. The preflight snapshot is stale by now — flows queued
      // ahead (or a dialog left open) changed the UTXO set — so re-read
      // before choosing the first pair. The dialog is not shown again.
      this.assertWalletContext(walletContext);
      status = await this.loadStatus(request, remainingTokenAmount);
      if (request.consolidateOnly && status.reason === 'incompatible_sources') {
        ({ status, tokenAmount: remainingTokenAmount } =
          await this.retargetCompatibleBalance(
            request,
            status,
            remainingTokenAmount,
          ));
      }
      // Last cancellation point before spend: the card was dismissible until
      // here, and nothing has been spent yet.
      if (ticket.cancelled) {
        request.onCancelled?.();
        return undefined;
      }
      let completedTransactions = 0;
      let chunkedActionStarted = false;
      let lastActionId: string | undefined;

      const awaitQueuedAction = async (
        actionId: string,
        completion: ReturnType<
          Kcc20WalletOperationQueueService['enqueueActionAndWait']
        >['completion'],
      ): Promise<void> => {
        try {
          await this.awaitIndexed(actionId, completion, request.onProgress);
        } catch (error) {
          queuedActionFailed = true;
          throw error;
        }
      };

      while (true) {
        this.assertWalletContext(walletContext);
        this.assertActionable(request, status);
        this.assertTransactionLimit(
          status,
          completedTransactions,
          request.consolidateOnly,
        );
        if (status.reason === 'not_required') {
          if (request.consolidateOnly) {
            return lastActionId;
          }
          if (ticket.cancelled) {
            request.onCancelled?.();
            return lastActionId;
          }
          handOffCard();
          if (!chunkedActionStarted) {
            this.assertWalletContext(walletContext);
            return this.enqueueOriginal(request, walletContext);
          }
          this.assertWalletContext(walletContext);
          const completion = this.enqueueAndWait(
            request,
            remainingTokenAmount,
            request.title,
            walletContext,
          );
          lastActionId = completion.id;
          await awaitQueuedAction(completion.id, completion.completion);
          this.assertWalletContext(walletContext);
          return lastActionId;
        }
        if (
          completedTransactions >=
          Kcc20TokenConsolidationFlowService.MAX_MERGES_PER_FLOW
        ) {
          throw new Error(
            'Token preparation exceeded the safe transaction limit. Refresh balances and retry.',
          );
        }

        if (status.reason === 'ready') {
          const previousSnapshotId = status.snapshotId;
          const sourceOutpoints = status.nextSourceOutpoints!;
          this.assertWalletContext(walletContext);
          if (cardHeld) {
            const waitingMessage = 'Waiting for balance preparation…';
            this.queue.updatePendingFlow(ticket.cardId, {
              message: waitingMessage,
            });
            ticket.onProgress?.(waitingMessage);
          }
          const queued = this.queue.enqueueActionAndWait({
            tokenId: request.tokenId,
            title: WALLET_OPERATION_LABELS.prepareBalance,
            internal: request.internal,
            build: () =>
              this.buildForWalletContext(walletContext, () =>
                this.tokenActionApi.buildConsolidation(request.tokenId, {
                  asset: request.asset,
                  ...(request.wrappedMarketId
                    ? { wrappedMarketId: request.wrappedMarketId }
                    : {}),
                  sourceOutpoints,
                }),
              ),
          });
          request.onQueued?.(queued.id);
          lastActionId = queued.id;
          await awaitQueuedAction(queued.id, queued.completion);
          if (ticket.cancelled) {
            request.onCancelled?.();
            return lastActionId;
          }
          this.assertWalletContext(walletContext);
          completedTransactions += 1;
          status = await this.waitForProjectionChange(
            request,
            remainingTokenAmount,
            previousSnapshotId,
            walletContext,
          );
          continue;
        }

        // Sequential unwrap chunks ARE the originating action — release the
        // placeholder so wrap/unwrap does not sit beside a same-titled chunk.
        handOffCard();
        const previousSnapshotId = status.snapshotId;
        const nextActionAmount = BigInt(status.nextActionAmount!);
        const requestedAmount = BigInt(status.requestedAmount);
        const chunkTokenAmount = formatKcc20BaseUnitsForDisplay(
          nextActionAmount,
          status.decimals,
        );
        const queued = this.enqueueAndWait(
          request,
          chunkTokenAmount,
          `${request.title} (${chunkTokenAmount} ${request.tokenLabel})`,
          walletContext,
        );
        lastActionId = queued.id;
        await awaitQueuedAction(queued.id, queued.completion);
        this.assertWalletContext(walletContext);
        chunkedActionStarted = true;
        completedTransactions += 1;

        const remainingAmount = requestedAmount - nextActionAmount;
        if (remainingAmount <= 0n) {
          return lastActionId;
        }
        remainingTokenAmount = formatKcc20BaseUnitsForDisplay(
          remainingAmount,
          status.decimals,
        );
        status = await this.waitForProjectionChange(
          request,
          remainingTokenAmount,
          previousSnapshotId,
          walletContext,
        );
      }
    } catch (error) {
      handOffCard();
      // A dismissed click must not resurface as a failure — the user already
      // withdrew it while its status check was still in flight.
      if (ticket.cancelled) {
        request.onCancelled?.();
        return undefined;
      }
      // A queued merge/action that already failed in the overlay is the
      // failure card. Recording another with the originating title (Wrap
      // tokens) is what left two rows after a failed consolidation.
      if (!queuedActionFailed) {
        // Everything above can throw BEFORE (or in between) enqueues: the
        // status load, the actionability/transaction-limit guards, a wallet
        // switch mid-flow, the projection-refresh timeout. Those rejections
        // used to reach the user only as inline page text, so the overlay
        // showed nothing at all (#220). Record a card, then rethrow so the
        // calling page still surfaces its own in-context error.
        const message = this.errorMessage(error);
        this.queue.recordPreflightFailure({
          tokenId: request.tokenId,
          title: request.title,
          message,
          retry: () => void this.execute(request).catch(() => undefined),
          internal: request.internal,
        });
        request.onProgress?.(message);
      }
      throw error;
    } finally {
      handOffCard();
      // Covers every exit while still preflighting (debounce aside): cancel,
      // dialog dismissal, nothing-to-do, preflight failure.
      this.preflightFlows.delete(ticket);
      if (laneHeld) {
        this.activeFlows.delete(tokenKey);
      }
      this.syncFlowKeys();
      this.maybeDrainFlows();
    }
  }

  /**
   * Accept or ignore a click for debounce purposes. Keyed on token+asset+title
   * so two DIFFERENT intents (wrap then sell) never swallow each other; only a
   * repeat of the same button within the window is ignored.
   */
  private isDoubleClick(
    tokenKey: string,
    request: Kcc20ConsolidationFlowRequest,
  ): boolean {
    const debounceKey = `${tokenKey}:${request.asset}:${request.title}`;
    const now = this.now();
    const last = this.lastAcceptedClickAt.get(debounceKey);
    if (
      last !== undefined &&
      now - last < Kcc20TokenConsolidationFlowService.DOUBLE_CLICK_IGNORE_MS
    ) {
      return true;
    }
    this.lastAcceptedClickAt.set(debounceKey, now);
    return false;
  }

  /** Join the token's flow lane and resolve when it is this flow's turn. */
  private waitForFlowTurn(
    ticket: PendingFlowTicket,
  ): Promise<'run' | 'cancelled'> {
    if (ticket.cancelled) {
      return Promise.resolve('cancelled');
    }
    const turn = new Promise<'run' | 'cancelled'>((resolve) => {
      ticket.resolveTurn = resolve;
    });
    const lane = this.queuedFlows.get(ticket.tokenKey) ?? [];
    lane.push(ticket);
    this.queuedFlows.set(ticket.tokenKey, lane);
    this.preflightFlows.delete(ticket);
    const queuedMessage = 'Queued — waiting for the token to be free.';
    this.queue.updatePendingFlow(ticket.cardId, { message: queuedMessage });
    ticket.onProgress?.(queuedMessage);
    this.syncFlowKeys();
    this.maybeDrainFlows();
    return turn;
  }

  /**
   * Start the next queued flow for every token whose lane is fully quiet: no
   * flow currently replaying, and no in-flight wallet operation (queued or
   * still-indexing) for the token. That stillness is what makes the replay's
   * fresh status snapshot trustworthy.
   */
  private maybeDrainFlows(): void {
    for (const [tokenKey, lane] of [...this.queuedFlows]) {
      if (!lane.length) {
        this.queuedFlows.delete(tokenKey);
        continue;
      }
      if (this.activeFlows.has(tokenKey) || this.queue.isTokenBusy(tokenKey)) {
        continue;
      }
      const ticket = lane.shift()!;
      if (!lane.length) {
        this.queuedFlows.delete(tokenKey);
      }
      this.activeFlows.set(tokenKey, ticket.asset);
      const preparingMessage = 'Preparing the token action…';
      this.queue.updatePendingFlow(ticket.cardId, {
        phase: 'building',
        message: preparingMessage,
      });
      ticket.onProgress?.(preparingMessage);
      this.syncFlowKeys();
      ticket.resolveTurn?.('run');
    }
  }

  /** Dismissal hook from the overlay card: withdraw the flow wherever it is. */
  private cancelTicket(ticket: PendingFlowTicket): void {
    ticket.cancelled = true;
    this.preflightFlows.delete(ticket);
    const lane = this.queuedFlows.get(ticket.tokenKey);
    if (lane) {
      const index = lane.indexOf(ticket);
      if (index >= 0) {
        lane.splice(index, 1);
      }
      if (!lane.length) {
        this.queuedFlows.delete(ticket.tokenKey);
      }
    }
    this.syncFlowKeys();
    // Settles the lane wait for a queued flow; a no-op for one already
    // running (a promise only settles once) — a started replay finishes.
    ticket.resolveTurn?.('cancelled');
  }

  private syncFlowKeys(): void {
    const next = new Map<string, Set<Kcc20ConsolidationAsset>>();
    const add = (key: string, asset: Kcc20ConsolidationAsset) => {
      const assets = next.get(key) ?? new Set<Kcc20ConsolidationAsset>();
      assets.add(asset);
      next.set(key, assets);
    };
    for (const ticket of this.preflightFlows) {
      add(ticket.tokenKey, ticket.asset);
    }
    for (const [key, asset] of this.activeFlows) {
      add(key, asset);
    }
    for (const [key, lane] of this.queuedFlows) {
      for (const ticket of lane) {
        add(key, ticket.asset);
      }
    }
    this.flowKeys.set(next);
  }

  private enqueueOriginal(
    request: Kcc20ConsolidationFlowRequest,
    walletContext: ConsolidationWalletContext,
  ): string {
    if (!request.build) {
      throw new Error('The token action builder is missing.');
    }
    const id = this.queue.enqueueAction({
      tokenId: request.tokenId,
      title: request.title,
      internal: request.internal,
      onSubmitted: request.onSubmitted,
      build: () => this.buildForWalletContext(walletContext, request.build!),
    });
    request.onQueued?.(id);
    return id;
  }

  private loadStatus(
    request: Kcc20ConsolidationFlowRequest,
    tokenAmount: string,
  ): Promise<Kcc20ConsolidationStatus> {
    return firstValueFrom(
      this.tokenActionApi.getConsolidationStatus(request.tokenId, {
        asset: request.asset,
        tokenAmount,
        ...(request.operation ? { operation: request.operation } : {}),
        ...(request.wrappedMarketId
          ? { wrappedMarketId: request.wrappedMarketId }
          : {}),
      }),
    ).catch((error: unknown) => {
      throw new Error(this.errorMessage(error));
    });
  }

  private async retargetCompatibleBalance(
    request: Kcc20ConsolidationFlowRequest,
    status: Kcc20ConsolidationStatus,
    tokenAmount: string,
  ): Promise<{ status: Kcc20ConsolidationStatus; tokenAmount: string }> {
    if (
      !request.consolidateOnly ||
      status.reason !== 'incompatible_sources' ||
      BigInt(status.compatibleAggregateAmount) <= 0n
    ) {
      return { status, tokenAmount };
    }
    const compatibleTokenAmount = formatKcc20BaseUnitsForDisplay(
      BigInt(status.compatibleAggregateAmount),
      status.decimals,
    );
    return {
      status: await this.loadStatus(request, compatibleTokenAmount),
      tokenAmount: compatibleTokenAmount,
    };
  }

  private assertActionable(
    request: Kcc20ConsolidationFlowRequest,
    status: Kcc20ConsolidationStatus,
  ): void {
    if (!/^[0-9a-f]{64}$/i.test(status.snapshotId)) {
      throw new Error(
        'The backend did not return a valid token-source snapshot. Refresh after the backend deployment is complete.',
      );
    }
    if (status.reason === 'insufficient_balance') {
      throw new Error(
        status.asset === 'orderbook'
          ? 'The wallet does not have enough spendable trading balance. Tokens in open sell orders must be cancelled before they can be used.'
          : 'The wallet does not have enough token balance.',
      );
    }
    if (status.reason === 'insufficient_reserve') {
      throw new Error(
        'The wrapper does not currently have enough indexed reserve to complete this unwrap. Refresh after pending wrap transactions are indexed.',
      );
    }
    if (status.reason === 'incompatible_sources') {
      throw new Error(
        'The total balance spans incompatible covenant versions and cannot be consolidated together.',
      );
    }
    if (
      status.reason === 'ready' &&
      (!status.canConsolidate ||
        !status.nextSourceOutpoints ||
        status.nextSourceOutpoints.length < 2 ||
        status.nextSourceOutpoints.length > 8)
    ) {
      throw new Error(
        'The indexer did not return a compatible token consolidation batch.',
      );
    }
    if (
      status.reason === 'action_chunking_required' &&
      (request.operation !== 'unwrap' ||
        !request.buildForTokenAmount ||
        !status.nextActionAmount ||
        BigInt(status.nextActionAmount) <= 0n ||
        BigInt(status.nextActionAmount) >= BigInt(status.requestedAmount))
    ) {
      throw new Error(
        'The indexer did not return a valid sequential unwrap plan.',
      );
    }
  }

  private assertTransactionLimit(
    status: Kcc20ConsolidationStatus,
    completedTransactions = 0,
    consolidateOnly = false,
  ): void {
    const estimatedTransactions =
      completedTransactions +
      status.estimatedMergeTransactions +
      (consolidateOnly
        ? 0
        : (status.estimatedActionTransactions ?? (status.required ? 1 : 0)));
    if (
      estimatedTransactions >
      Kcc20TokenConsolidationFlowService.MAX_MERGES_PER_FLOW
    ) {
      throw new Error(
        `This action requires about ${estimatedTransactions} wallet approvals, above the safe limit of ${Kcc20TokenConsolidationFlowService.MAX_MERGES_PER_FLOW}. Use a smaller amount and retry.`,
      );
    }
  }

  private async waitForProjectionChange(
    request: Kcc20ConsolidationFlowRequest,
    tokenAmount: string,
    previousSnapshotId: string,
    walletContext: ConsolidationWalletContext,
  ): Promise<Kcc20ConsolidationStatus> {
    for (
      let attempt = 0;
      attempt < Kcc20TokenConsolidationFlowService.PROJECTION_POLL_ATTEMPTS;
      attempt += 1
    ) {
      this.assertWalletContext(walletContext);
      const status = await this.loadStatus(request, tokenAmount);
      this.assertWalletContext(walletContext);
      if (status.snapshotId !== previousSnapshotId) {
        return status;
      }
      if (
        attempt + 1 <
        Kcc20TokenConsolidationFlowService.PROJECTION_POLL_ATTEMPTS
      ) {
        await this.delay(
          Kcc20TokenConsolidationFlowService.PROJECTION_POLL_DELAY_MS,
        );
      }
    }
    throw new Error(
      'The transaction is indexed, but token balances have not refreshed yet. Wait for the indexer projection and retry.',
    );
  }

  private captureWalletContext(): ConsolidationWalletContext {
    const wallet = this.walletManager.signedWalletSignal();
    if (!wallet?.walletAddress || !wallet.kcc20Owner) {
      throw new Error('Connect the wallet before preparing this token action.');
    }
    return {
      walletAddress: wallet.walletAddress.trim().toLowerCase(),
      owner: wallet.kcc20Owner.trim().toLowerCase(),
    };
  }

  private assertWalletContext(expected: ConsolidationWalletContext): void {
    const current = this.walletManager.signedWalletSignal();
    if (
      !current ||
      current.walletAddress.trim().toLowerCase() !== expected.walletAddress ||
      current.kcc20Owner.trim().toLowerCase() !== expected.owner
    ) {
      throw new Error(
        'The connected wallet changed during token preparation. No further transaction was queued; restart the action with the current wallet.',
      );
    }
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  private now(): number {
    return Date.now();
  }

  private enqueueAndWait(
    request: Kcc20ConsolidationFlowRequest,
    tokenAmount: string,
    title: string,
    walletContext: ConsolidationWalletContext,
  ) {
    const queued = this.queue.enqueueActionAndWait({
      tokenId: request.tokenId,
      title,
      internal: request.internal,
      onSubmitted: request.onSubmitted,
      build: () =>
        this.buildForWalletContext(walletContext, () =>
          request.buildForTokenAmount!(tokenAmount),
        ),
    });
    request.onQueued?.(queued.id);
    return queued;
  }

  private buildForWalletContext<T>(
    expected: ConsolidationWalletContext,
    build: () => T,
  ): T {
    this.assertWalletContext(expected);
    return build();
  }

  /**
   * Wraps {@link assertIndexed} with a live progress watcher for internal
   * flows: while `actionId` is in the queue (build→sign→submit), its
   * `Kcc20OperationView.message` — "Opening wallet…", "Approve in your
   * wallet…", etc. — is forwarded to `onProgress` via {@link forwardProgress},
   * driven by the effect in the constructor. No-op when `onProgress` is unset
   * (every non-internal caller).
   */
  private async awaitIndexed(
    actionId: string,
    completionPromise: ReturnType<
      Kcc20WalletOperationQueueService['enqueueActionAndWait']
    >['completion'],
    onProgress?: (message: string) => void,
  ): Promise<void> {
    if (onProgress) {
      this.progressWatchers.set(actionId, onProgress);
    }
    try {
      await this.assertIndexed(completionPromise);
    } finally {
      this.progressWatchers.delete(actionId);
    }
  }

  private async assertIndexed(
    completionPromise: ReturnType<
      Kcc20WalletOperationQueueService['enqueueActionAndWait']
    >['completion'],
  ): Promise<void> {
    const completion = await completionPromise;
    if (completion.status !== 'indexed') {
      throw new Error(
        completion.message || 'Token preparation did not reach indexed state.',
      );
    }
  }

  private tokenKey(tokenId: string): string {
    return tokenId.trim().toLowerCase();
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as { message?: string } | undefined;
      return body?.message || error.message || 'Consolidation status failed.';
    }
    return error instanceof Error
      ? error.message
      : 'Consolidation status failed.';
  }
}
